import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Internationalization API",
  "title": "Internationalization"
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Alert = makeShortcode("Alert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="internationalization" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  React Magma exposes an I18nContext object and an object named defaultI18n that
  contains the default values used by the context provider.
      </LeadParagraph>
      <h2>{`Usage`}</h2>
      <p>{`You can supply your own object or override specific keys in the object to provide translations for strings in the components.`}</p>
      <p>{`There are some values in the context that are used to localize date and time. These are `}<inlineCode parentName="p">{`locale`}</inlineCode>{`, `}<inlineCode parentName="p">{`dateFormat`}</inlineCode>{`, `}<inlineCode parentName="p">{`timeFormat`}</inlineCode>{`, and `}<inlineCode parentName="p">{`datePicker.startOfWeek`}</inlineCode>{`. These keys have specific types listed below, while the rest are `}<inlineCode parentName="p">{`strings`}</inlineCode>{`:`}</p>
      <ul>
        <li parentName="ul">
          <p parentName="li"><inlineCode parentName="p">{`locale`}</inlineCode>{` is a `}<inlineCode parentName="p">{`date-fns`}</inlineCode>{` locale object. You can `}<a parentName="p" {...{
              "href": "https://github.com/date-fns/date-fns/tree/master/src/locale"
            }}>{`see the available objects here`}</a>{`.
Example of a locale import: `}<inlineCode parentName="p">{`import { es } from 'date-fns/locale'`}</inlineCode>{`.`}</p>
          <Alert variant="warning" mdxType="Alert">
  The locale in the default object is null to prevent the date-fns library
  from being bundled in your project when you are not using any of its
  functionality. When you use a component that depends on the date-fns
  library, React Magma will default your locale to enUS.
          </Alert>
        </li>
        <li parentName="ul">
          <p parentName="li"><inlineCode parentName="p">{`dateFormat`}</inlineCode>{` is a list of acceptable date formats as strings: `}<inlineCode parentName="p">{`'MM/dd/yyyy' | 'dd/MM/yyyy' | 'yyyy/MM/dd' | 'yyyy/dd/MM'`}</inlineCode>{`.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><inlineCode parentName="p">{`timeFormat`}</inlineCode>{` is either the string `}<inlineCode parentName="p">{`'12'`}</inlineCode>{` or `}<inlineCode parentName="p">{`'24'`}</inlineCode>{`.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><inlineCode parentName="p">{`datePicker.startOfWeek`}</inlineCode>{` is the string of the day of the week that you want your week to start on: `}<inlineCode parentName="p">{`'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'`}</inlineCode></p>
        </li>
      </ul>
      <h2>{`Default Context Object`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx",
          "metastring": "noCode noCopy noCodeSandbox",
          "noCode": true,
          "noCopy": true,
          "noCodeSandbox": true
        }}>{`import React from 'react';

import { defaultI18n } from 'react-magma-dom';

export function Example() {
  return (
    <pre>
      <code>{JSON.stringify(defaultI18n, null, 2)}</code>
    </pre>
  );
}
`}</code></pre>
      <h2>{`Example Usage`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { DatePicker, I18nContext, defaultI18n, es } from 'react-magma-dom';

export function Example() {
  return (
    <I18nContext.Provider
      value={{
        ...defaultI18n,
        months: {
          long: {
            january: 'Enero',
            february: 'Febrero',
            march: 'Marzo',
            april: 'Abril',
            may: 'Mayo',
            june: 'Junio',
            july: 'Julio',
            august: 'Agosto',
            september: 'Septiembre',
            october: 'Octubre',
            november: 'Noviembre',
            december: 'Diciembre',
          },
        },
        days: {
          ...defaultI18n.days,
          short: {
            sunday: 'Dom',
            monday: 'Lun',
            tuesday: 'Mar',
            wednesday: 'Mié',
            thursday: 'Jue',
            friday: 'Vie',
            saturday: 'Sáb',
          },
        },
        datePicker: {
          startOfWeek: 'monday',
          calendarIconAriaLabel: 'Calendario',
          calendarOpenAnnounce:
            'El widget de calendario ahora está abierto. Presione la tecla de signo de interrogación para obtener los atajos de teclado para cambiar las fechas.',
          calendarCloseAriaLabel: 'Cerrar calendario',
          previousMonthAriaLabel: 'Mes Anterior',
          nextMonthAriaLabel: 'Próximo Mes',
          disabledDayAriaLabel: 'No disponible. ',
          selectedDayAriaLabel: '(Seleccionado)',
          todayAriaLabel: '(Hoy)',
          today: 'Hoy',
          navigateToCurrentDateAriaLabel: 'Navegar al mes y año actual',
          backToCalendarAriaLabel: 'Volver al calendario',
          backToCalendar: 'Volver al calendario',
          selectMonth: 'Seleccione un mes',
          selectYear: 'Seleccione un año',
          helpModal: {
            header: 'Atajos de teclado',
            helpButtonAriaLabel: 'Ayuda de calendario',
            enter: {
              ariaLabel: 'Introducir clave',
              explanation: 'Seleccione la fecha en foco.',
            },
            rightAndLeftArrowKeys: {
              ariaLabel: 'Teclas de flecha derecha e izquierda',
              explanation:
                'Muévase hacia atrás (izquierda) y hacia adelante (derecha) un día.',
            },
            upAndDownArrowKeys: {
              ariaLabel: 'teclas de flecha arriba y abajo',
              explanation:
                'Muévase hacia atrás (arriba) y hacia adelante (abajo) una semana.',
            },
            pageUpAndPageDownKeys: {
              ariaLabel: 'teclas de página arriba y página abajo',
              displayValue: 'PGAR/PGAB',
              explanation: 'Cambiar de mes.',
            },
            homeAndEndKeys: {
              ariaLabel: 'Teclas de inicio y fin',
              displayValue: 'CASA/FINAL',
              explanation: 'Ir al primer o último día de una semana.',
            },
            escape: {
              ariaLabel: 'Tecla de escape',
              displayValue: 'ESCAPAR',
              explanation: 'Regresar al campo de entrada de fecha.',
            },
          },
        },
        locale: es,
      }}
    >
      <DatePicker labelText="Español" />
    </I18nContext.Provider>
  );
}
`}</code></pre>
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      